import { Link } from "react-router-dom";
import { MapPin, Clock, CalendarDays, Users, Check, X } from "lucide-react";
import { StatusPill, MatchBadge, UrgentPill } from "@/components/StatusPill";
import { fmtDate, fmtTime, payLabel } from "@/lib/api";

export const MatchReasons = ({ reasons = [], limit }) => (
  <ul className="space-y-1.5">
    {(limit ? reasons.slice(0, limit) : reasons).map((r) => (
      <li key={r.label} className={`flex items-start gap-2 text-sm ${r.ok ? "text-slate-700" : "text-slate-400"}`}>
        {r.ok ? (
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        ) : (
          <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
        )}
        <span>{r.label}</span>
      </li>
    ))}
  </ul>
);

export const ShiftCard = ({ shift, to, actionLabel = "View", showMatch = true, right }) => (
  <div
    className="card-surface group flex flex-col gap-4 p-5 transition-all hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-md"
    data-testid={`shift-card-${shift.shift_id}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-lg font-semibold leading-tight text-slate-900">
          {shift.worker_role} Coverage
        </h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
          <MapPin className="h-3.5 w-3.5" />
          {shift.location_label || shift.city}
          {shift.zip_code && <span className="text-slate-400">{shift.zip_code}</span>}
          {shift.distance != null && (
            <span className="text-slate-400">· {shift.distance} mi away</span>
          )}
        </p>
        {shift.pharmacy?.name_hidden && (
          <p className="mt-1 text-xs text-slate-400" data-testid={`hidden-pharmacy-${shift.shift_id}`}>
            Pharmacy name hidden until confirmed
          </p>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        {shift.urgent && <UrgentPill testId={`urgent-${shift.shift_id}`} />}
        {showMatch && shift.match_score != null && <MatchBadge score={shift.match_score} />}
        <StatusPill status={shift.status} />
      </div>
    </div>

    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
      <span className="flex items-center gap-1.5">
        <CalendarDays className="h-4 w-4 text-slate-400" />
        {fmtDate(shift.date, { weekday: "short", month: "short", day: "numeric" })}
      </span>
      <span className="flex items-center gap-1.5">
        <Clock className="h-4 w-4 text-slate-400" />
        {fmtTime(shift.start_time)} – {fmtTime(shift.end_time)}
      </span>
      <span className="font-display text-base font-semibold text-slate-900">{payLabel(shift)}</span>
      {shift.interest_count > 0 && (
        <span className="flex items-center gap-1.5 text-slate-500">
          <Users className="h-4 w-4 text-slate-400" />
          {shift.interest_count} interested
        </span>
      )}
    </div>

    {shift.software_required?.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {shift.software_required.map((s) => (
          <span key={s} className="pill border-slate-200 bg-slate-50 text-slate-600">
            {s}
          </span>
        ))}
      </div>
    )}

    {showMatch && shift.match_reasons && (
      <MatchReasons reasons={shift.match_reasons.filter((r) => r.ok)} limit={3} />
    )}

    <div className="mt-auto flex items-center justify-between gap-3 pt-1">
      {right}
      {to && (
        <Link to={to} className="btn-primary ml-auto" data-testid={`shift-view-${shift.shift_id}`}>
          {actionLabel}
        </Link>
      )}
    </div>
  </div>
);
